import {
  RolePermissions,
  resolveAllowedRoleAndPermissions,
} from 'components/employees/employeeEditPolicy';

interface EmployeeSubmitState {
  id?: number | null;
  takesAppointments?: boolean;
  employeeOffers?: unknown[];
  employeeCategoryId?: number | null;
  jobTitle?: string;
  notes?: string;
  role?: string | null;
  permissions?: string[];
  [key: string]: unknown;
}

interface PersistedEmployeeData {
  externalCalendarUri?: string | null;
  employeeOffers?: unknown[];
  employeeCategoryId?: number | null;
  jobTitle?: string;
  notes?: string;
  role?: string | null;
  permissions?: string[];
}

interface BuildEmployeeSubmitPayloadParams {
  employee: EmployeeSubmitState;
  persistedData: PersistedEmployeeData;
  roleChangeAllowed: boolean;
  actorPermissions?: string[];
  permissionsByRoles?: RolePermissions[];
}

export function buildEmployeeSubmitPayload({
  employee,
  persistedData,
  roleChangeAllowed,
  actorPermissions = [],
  permissionsByRoles = [],
}: BuildEmployeeSubmitPayloadParams): Record<string, unknown> {
  const base = {
    ...employee,
    externalCalendarUri: persistedData.externalCalendarUri,
    employeeOffers: employee.takesAppointments
      ? employee.employeeOffers
      : persistedData.employeeOffers,
    employeeCategoryId: employee.takesAppointments
      ? employee.employeeCategoryId
      : persistedData.employeeCategoryId,
    jobTitle: employee.takesAppointments ? employee.jobTitle : persistedData.jobTitle,
    notes: employee.takesAppointments ? employee.notes : persistedData.notes,
  };

  if (!roleChangeAllowed) {
    if (employee.id == null) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { role, permissions, ...withoutRolePerms } = base;
      const resolved = resolveAllowedRoleAndPermissions(
        actorPermissions,
        null,
        [],
        permissionsByRoles,
        { isCreate: true },
      );
      return {
        ...withoutRolePerms,
        role: resolved.role,
        permissions: resolved.permissions,
      };
    }

    return {
      ...base,
      role: persistedData.role,
      permissions: persistedData.permissions ?? [],
    };
  }

  const resolved = resolveAllowedRoleAndPermissions(
    actorPermissions,
    employee.role ?? null,
    employee.permissions ?? [],
    permissionsByRoles,
    {
      isCreate: employee.id == null,
      currentTarget:
        employee.id != null
          ? {
              role: persistedData.role ?? null,
              permissions: persistedData.permissions ?? [],
            }
          : undefined,
    },
  );

  return {
    ...base,
    role: resolved.role,
    permissions: resolved.permissions,
  };
}
